import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { TradePlanData } from '../../../../../../classes/tradePlan/tradePlanData';
import { useTradePlanFormStore } from '../../../../../../classes/tradePlan/tradePlanProvider';
import { colors } from '../../../../../../colorVars';
import { BoxSwitch } from '../../../../../atomic/atomicComponentLibrary/switches/boxSwitch/BoxSwitch';
import { BorderAndLabel } from '../../../../../atomic/atomicComponentLibrary/utility/borderAndLabel/BorderAndLabel';
import { Body4, fontSizes } from '../../../../../atomic/text/Text';
import style from './Options.module.css';
import { requestOptionExpirationDates } from '../../../../../../stores/websocketStore/utils/outgoingMessages/optionexpirations/requestOptionExpirationDates';

interface Props {
  margin?: string;
}
export const Options = observer(({ margin }: Props) => {
  const [strategy, setStrategy] = useState('TIGHTEST_SPREAD');
  const tradePlanForm: TradePlanData = useTradePlanFormStore();
  const optionManagement = tradePlanForm.optionManagement;
  const symbol = tradePlanForm.symbol;
  const dataSource = tradePlanForm.dataSource;
  const notExpiringToday = optionManagement.notExpiringToday;
  const requestKey = optionManagement.requestKey;

  useEffect(() => {
    if (strategy && tradePlanForm && requestKey) {
      optionManagement.setOptionChainStrategy(strategy);
    }
  }, [strategy, tradePlanForm, requestKey, optionManagement]);

  useEffect(() => {
    if (symbol && dataSource && requestKey) {
      requestOptionExpirationDates(
        requestKey,
        symbol,
        dataSource,
        notExpiringToday
      );
    }
  }, [symbol, dataSource, requestKey, notExpiringToday]);

  return (
    <BorderAndLabel
      backgroundColor="black"
      margin={margin}
      label="Options"
      color="white"
      fontSize={fontSizes.body3}
    >
      <div className={style.innerDiv}>
        <Body4 color={colors.lightGrey} margin="0 0 2px 0">
          Buy/Sell
        </Body4>
        <BoxSwitch
          margin="0 3px 5px 5px"
          setValue={optionManagement.setBuyOrSellOption}
          notSelectedColor="grey"
          selectedColor="green"
          color="white"
          labels={['Buy', 'Sell']}
          values={['LONG', 'SHORT']}
          controlledValue={optionManagement.buyOrSellOption}
          title="Calls"
          fontSize={fontSizes.body4}
        />
        <Body4 color={colors.lightGrey} margin="0 0 2px 0">
          Calls
        </Body4>
        <BoxSwitch
          margin="0 3px 5px 5px"
          setValue={optionManagement.setCallsFilter}
          notSelectedColor="grey"
          selectedColor="green"
          color="white"
          labels={['None', 'All', 'Front']}
          values={['none', 'all', 'front']}
          controlledValue={optionManagement.callsFilter}
          title="Calls"
          fontSize={fontSizes.body4}
        />
        <Body4 color={colors.lightGrey} margin="0 0 2px 0">
          Puts
        </Body4>
        <BoxSwitch
          margin="0 3px 5px 5px"
          setValue={optionManagement.setPutsFilter}
          notSelectedColor="grey"
          selectedColor="green"
          color="white"
          labels={['None', 'All', 'Front']}
          values={['none', 'all', 'front']}
          controlledValue={optionManagement.putsFilter}
          title="Puts"
          fontSize={fontSizes.body4}
        />
        <Body4 color={colors.lightGrey} margin="0 0 2px 0">
          Strategy
        </Body4>
        <BoxSwitch
          margin="0 3px 5px 5px"
          setValue={setStrategy}
          notSelectedColor="grey"
          selectedColor="green"
          color="white"
          labels={['Tight', 'OTM1', 'ITM1']}
          values={['TIGHTEST_SPREAD', 'OTM1', 'ITM1']}
          controlledValue={strategy}
          title="Puts"
          fontSize={fontSizes.body4}
        />
      </div>
    </BorderAndLabel>
  );
});

// add not expiring today field
// order of operations to select options
/**
 * detect change of dataSource, symbol (re-requests the expiration dates)
 * request expiration dates, recieve all dates
 * default to first two(remove first expiration if notExpiringToday is true and first date is today, if so work with 1 and 2)
 * allow user to add or remove expiration dates
 * request option chains for each expiration date selected(detect changes to selected expiration dates)
 * every 5-15 seconds update option chain with request above
 * around 2000 max
 * add selected options to tradePlanData
 */
